/**
 * BooksPage — /books. Index of all multi-chapter series ("books").
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Film, Loader2, BookOpen } from "lucide-react";
import { useTranslation } from "@/i18n";
import LanguagePicker from "@/LanguagePicker";
import { mediaUrl } from "@/mediaUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDuration(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

export default function BooksPage() {
  const [t] = useTranslation();
  const [books, setBooks] = useState(null);

  useEffect(() => {
    axios.get(`${API}/books`)
      .then(({ data }) => setBooks(data.books || []))
      .catch(() => setBooks([]));
  }, []);

  return (
    <div className="min-h-screen" data-testid="books-page">
      <header className="px-6 md:px-10 py-6 md:py-8 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/brand-s.png" alt="Storylo" className="w-9 h-9 rounded-full" data-testid="brand-logo" />
            <div>
              <div className="font-display text-lg leading-none">Storylo</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)] mt-1">
                {t("tagline")}
              </div>
            </div>
          </a>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <a href="/" className="text-[color:var(--muted)] hover:text-white transition-colors">{t("nav_all")}</a>
            <a href="/books" className="text-white">{t("nav_books")}</a>
            <a href="/impact" className="text-[color:var(--muted)] hover:text-white transition-colors">{t("nav_impact")}</a>
            <LanguagePicker />
          </nav>
        </div>
      </header>

      <main className="px-6 md:px-10 py-12 md:py-16">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-3 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> {t("nav_books")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("books_title")}
          </h1>
          <p className="text-[color:var(--muted)] max-w-2xl mt-4 leading-relaxed text-base sm:text-lg">
            {t("books_sub")}
          </p>

          {books === null ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-[color:var(--accent)]" />
            </div>
          ) : books.length === 0 ? (
            <div className="mt-10 panel p-10 text-center text-[color:var(--muted)]">
              No books yet — multi-chapter series will appear here.
            </div>
          ) : (
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="books-grid">
              {books.map((b) => (
                <a
                  key={b.series_key}
                  href={`/book/${encodeURIComponent(b.series_key)}`}
                  className="panel overflow-hidden group block"
                  data-testid={`book-card-${b.series_key}`}
                >
                  <div className="aspect-video bg-white/[0.04] overflow-hidden">
                    {b.cover_url ? (
                      <img
                        src={mediaUrl(b.cover_url)}
                        alt={b.title}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-10 h-10 text-[color:var(--muted)] opacity-40" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="font-display text-lg leading-snug group-hover:text-[color:var(--accent)] transition-colors">
                      {b.title}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mt-2">
                      {b.chapter_count} {t("book_chapters")}
                      {b.total_duration_seconds > 0 && ` · ${fmtDuration(b.total_duration_seconds)}`}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
